import React, { FC, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import { useContributorData } from "./hooks";
import { DetailTable, PositionChart, GradeChart } from "./components";
import type { GradePayGapData } from "../../hooks";
import {
  SALARY_COMPONENT_OPTIONS,
  getSalaryComponentLabel,
  getSalaryComponentTooltip,
  type SalaryComponentType,
} from "../../../PaygapUploadData/hooks/regressionOptions";

import { Card, CardContent } from "@components/base";

export interface PositionRolePayGapData {
  position: string;
  menAverage: number;
  womenAverage: number;
  payGap: number;
  seniorityLevel: number;
}

export interface ContributorItem {
  key: string;
  value: string;
}

interface PaygapPositionRoleOverviewProps {
  data: PositionRolePayGapData[];
  gradeData?: GradePayGapData[];
  analysisResults?: PaygapMetricItemDto[] | null;
}

const PaygapPositionRoleOverview: FC<PaygapPositionRoleOverviewProps> = ({
  data,
  gradeData = [],
  analysisResults,
}) => {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState<"position" | "grade">("position");
  const [showDetail, setShowDetail] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [selectedGrading, setSelectedGrading] = useState<string>("all");
  const [selectedSalaryType, setSelectedSalaryType] =
    useState<SalaryComponentType | "all">("all");

  // Determine available grading types and salary types from the data
  const availableGradings = useMemo(
    () => [...new Set(gradeData.map((d) => d.gradeType))].sort(),
    [gradeData],
  );
  const availableSalaryTypes = useMemo(
    () => SALARY_COMPONENT_OPTIONS.filter((opt) =>
      gradeData.some((d) => d.salaryType === opt.value),
    ),
    [gradeData],
  );

  const { topContributorData, filteredContributorData } = useContributorData({
    analysisResults,
    filterText,
  });

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "position" | "grade" | null,
  ): void => {
    if (newMode !== null) {
      setViewMode(newMode);
      setShowDetail(false);
    }
  };

  const hasGradeData = gradeData.length > 0;

  return (
    <Card sx={{ p: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">
            {viewMode === "grade"
              ? t("paygap.payGapPerRole.titleByGrade")
              : t("paygap.payGapPerRole.title")}
          </Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {hasGradeData && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="position">
                  {t("paygap.chart.byPosition")}
                </ToggleButton>
                <ToggleButton value="grade">
                  {t("paygap.chart.byGrade")}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showDetail}
                  onChange={(e) => setShowDetail(e.target.checked)}
                  size="small"
                />
              }
              label={t("paygap.payGapPerRole.detail")}
            />
            <Tooltip title={t("paygap.payGapPerRole.infoTooltip")}>
              <IconButton size="small">
                <Info />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {viewMode === "grade" && hasGradeData && (
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            {/* Grading scenario dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t("paygap.chart.gradingScenario", "Grading Scenario")}</InputLabel>
              <Select
                size="small"
                value={selectedGrading}
                label={t("paygap.chart.gradingScenario", "Grading Scenario")}
                onChange={(e) => setSelectedGrading(e.target.value as string)}
              >
                <MenuItem value="all">
                  {t("paygap.chart.allGradings", "All Gradings")}
                </MenuItem>
                {availableGradings.map((g) => (
                  <MenuItem key={g} value={g}>
                    {t("paygap.chart.grading", "Grading")} {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Salary type dropdown */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("paygap.chart.salaryComponent", "Salary Component")}</InputLabel>
              <Select
                size="small"
                value={selectedSalaryType}
                label={t("paygap.chart.salaryComponent", "Salary Component")}
                onChange={(e) =>
                  setSelectedSalaryType(e.target.value as SalaryComponentType | "all")
                }
              >
                <MenuItem value="all">
                  {t("paygap.chart.allSalaryTypes", "All Salary Types")}
                </MenuItem>
                {availableSalaryTypes.map((opt) => {
                  const tooltip = getSalaryComponentTooltip(opt.value, i18n.language);

                  return (
                    <MenuItem key={opt.value} value={opt.value}>
                      {getSalaryComponentLabel(opt.value, i18n.language)}
                      {tooltip ? ` (${tooltip})` : ""}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {showDetail ? (
          <DetailTable
            topContributorData={topContributorData}
            filteredContributorData={filteredContributorData}
            filterText={filterText}
            onFilterChange={setFilterText}
          />
        ) : viewMode === "grade" && hasGradeData ? (
          <Box id="paygap-grade-chart">
            <GradeChart
              data={gradeData}
              selectedGrading={selectedGrading}
              selectedSalaryType={selectedSalaryType}
            />
          </Box>
        ) : (
          <Box id="paygap-position-role-chart">
            <PositionChart data={data} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PaygapPositionRoleOverview;
